import { ServiceTypeService } from '@aca-new/app/pages/book/shared/components/products/shared/services/service-type.service';
import { BookStrategyFactoryService } from '@aca-new/app/pages/book/shared/services/book-strategy/book-strategy-factory/book-strategy-factory.service';
import { BookFromPreviousOrderComponent } from '@aca-new/app/pages/bookings/pages/previous-order-item/components/book-from-previous-order/book-from-previous-order.component';
import { IBookFromPreviousOrderDialogData } from '@aca-new/app/pages/bookings/pages/previous-order-item/components/book-from-previous-order/shared/models/interfaces/book-from-previous-order-dialog-data.interface';
import { BOOKINGS } from '@aca-new/app/pages/bookings/shared/constants/bookings.constants';
import { IPreviousOrders } from '@aca-new/app/pages/bookings/shared/models/interfaces/bookings.interface';
import { BookingsService } from '@aca-new/app/pages/bookings/shared/services/bookings.service';
import { IPreviousOrder } from '@aca-new/app/pages/main/shared/components/aside/shared/components/aside-menu/share/models/interfaces/previous-order.interface';
import { AsideService } from '@aca-new/app/pages/main/shared/components/aside/shared/services/aside.service';
import { DialogService } from '@aca-new/app/shared/components/modal/shared/services/dialog.service';
import { SERVER_URL } from '@aca-new/app/shared/constants/app.constants';
import { GTM_ANALYTICS_EVENT_CATEGORY, GTM_RENEW_EVENTS } from '@aca-new/app/shared/constants/gtm-analytics.constants';
import { SUPPORT_SERVICE_TYPES } from '@aca-new/app/shared/constants/service-type.constants';
import { EBusinessSolution } from '@aca-new/app/shared/models/enums/business-solution.enum';
import { EServiceType } from '@aca-new/app/shared/models/enums/service-type.enum';
import { AppBookTypeService } from '@aca-new/app/shared/services/book-services/app-book-type/app-book-type.service';
import { QimaButtonService } from '@aca-new/app/shared/services/exported-services/qima-button/qima-button.service';
import { QimaColorService } from '@aca-new/app/shared/services/exported-services/qima-color/qima-color.service';
import { QimaSpinnerService } from '@aca-new/app/shared/services/exported-services/qima-spinner/qima-spinner.service';
import { QimaTooltipService } from '@aca-new/app/shared/services/exported-services/qima-tooltip/qima-tooltip.service';
import { GoogleAnalyticsService } from '@aca-new/app/shared/services/third-party-services/google-analytics/google-analytics.service';
import { AppTimeService } from '@aca-new/app/shared/services/time-services/app-time.service';
import { AppUserSettingService } from '@aca-new/app/shared/services/user-services/app-user-setting/app-user-setting.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EQimaIceCubeSize } from '@qima/ngx-qima';
import { sortBy } from 'lodash/index';
import { forkJoin } from 'rxjs';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'app-previous-order-item',
  templateUrl: './previous-order-item.component.html',
  styleUrls: ['./previous-order-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PreviousOrderItemComponent implements OnInit {
  @Input('previousOrderItemIsTitleVisible')
  public isTitleVisible: boolean = true;

  public iceCubeSize: EQimaIceCubeSize = EQimaIceCubeSize.SMALL;
  public previousOrders: IPreviousOrder[] = [];
  public isDataLoaded: boolean = false;
  public isViewMoreButtonVisible: boolean = false;

  public constructor(
    public readonly qimaTooltip: QimaTooltipService,
    public readonly spinnerService: QimaSpinnerService,
    public readonly qimaColor: QimaColorService,
    public readonly qimaButton: QimaButtonService,
    private readonly _bookingsService: BookingsService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _appTimeService: AppTimeService,
    private readonly _dialog: DialogService,
    private readonly _asideService: AsideService,
    private readonly _appUserSettingService: AppUserSettingService,
    private readonly _serviceTypeService: ServiceTypeService,
    private readonly _bookStrategyFactoryService: BookStrategyFactoryService,
    private readonly _appBookTypeService: AppBookTypeService,
    private readonly _router: Router,
    private readonly _googleAnalyticsService: GoogleAnalyticsService
  ) {}

  public ngOnInit(): void {
    this._initData();
  }

  public onPreviousOrderItemClick(item: IPreviousOrder): void {
    const serviceType = this._serviceTypeService.getServiceTypeCodeFromText(item.originData.serviceTypeText) as EServiceType;
    const businessSolution = this._appBookTypeService.getBookType(serviceType as EServiceType);

    /* eslint-disable @typescript-eslint/naming-convention -- GTM dataLayer keys */
    this._googleAnalyticsService.sendEvent({
      event: GTM_RENEW_EVENTS.previousOrderItemClick,
      event_category: GTM_ANALYTICS_EVENT_CATEGORY.userInteraction,
      event_label: 'Book From Previous Order Item',
      service_type: `${serviceType}`,
      business_solution: `${businessSolution}`,
    });
    /* eslint-enable @typescript-eslint/naming-convention */

    if (businessSolution === EBusinessSolution.INSPECTIONS) {
      const dialogData: IBookFromPreviousOrderDialogData = {
        businessSolution,
        preselectedOrder: item,
        searchKeyword: item.productNames.find((productName): boolean => !!productName?.trim())?.trim() ?? '',
        selectedServiceType: serviceType,
      };

      this._dialog.open(BookFromPreviousOrderComponent, {
        data: dialogData,
      });
      this._asideService.updateAsideMenuVisible(false);

      return;
    }

    const isAcaBookRoute: boolean = this._router.url.includes('/aca/book-');
    const currentStrategy = this._bookStrategyFactoryService.getCurrentStrategyService(businessSolution);

    currentStrategy.bookFromPreviousOrder({
      isAcaBookRoute,
      previousOrder: item,
      serviceType: serviceType as EServiceType,
    });
    this._asideService.updateAsideMenuVisible(false);
  }

  public onViewMoreClick(): void {
    this._dialog.open(BookFromPreviousOrderComponent);
    this._asideService.updateAsideMenuVisible(false);
  }

  private _initData(): void {
    this.isDataLoaded = false;
    forkJoin([
      this._bookingsService.getPreviousOrders$(`${SERVER_URL}/user/{userId}/psi-orders?page-size=10&page=1&sortType=desc&sortField=bookingDate`),
      this._bookingsService.getPreviousOrders$(`${SERVER_URL}/user/{userId}/audit-orders?service-type=${encodeURIComponent('ma,ea,stra,ctpat,enva,dr')}&page-size=10&page=1`),
      this._bookingsService.getPreviousOrders$(
        `${SERVER_URL}/user/{userId}/lt/orders?pageSize=10&orderStatus=${encodeURIComponent(BOOKINGS.LT.COPY_FROM_PREVIOUS_ORDER)}&pageNumber=1`
      ),
      this._bookingsService.getPreviousOrders$(`${SERVER_URL}/user/{userId}/wqs/orders?page-size=10&status=${encodeURIComponent('220')}&page=1&serviceTypes=100&isRecert=true`),
      this._bookingsService.getPreviousOrders$(`${SERVER_URL}/user/{userId}/wqs/orders?page-size=10&status=${encodeURIComponent('220')}&page=1&serviceTypes=110&isRecert=true`),
      this._bookingsService.getPreviousOrders$(`${SERVER_URL}/user/{userId}/wqs/orders?page-size=10&status=${encodeURIComponent('220')}&page=1&serviceTypes=120&isRecert=true`),
      this._bookingsService.getPreviousOrders$(`${SERVER_URL}/user/{userId}/wqs/orders?page-size=10&status=${encodeURIComponent('220')}&page=1&serviceTypes=130&isRecert=true`),
    ])
      .pipe(untilDestroyed(this))
      .subscribe((response): void => {
        let orders: IPreviousOrder[] = [];

        if (this._appUserSettingService.getEnableFoodCertification()) {
          const wqsOrders = (response[3] || [])
            .concat(response[4] || [])
            .concat(response[5] || [])
            .concat(response[6] || []);
          const allOrders = wqsOrders.map((order): IPreviousOrder => {
            return {
              iceCubeLabel: order.serviceTypeText,
              productNames: [order.refNumber],
              orderDate: `${order.auditDateFrom}`,
              originData: order,
              canActivate: true,
            };
          });
          const ggapOrders = allOrders.filter((order): boolean => order.iceCubeLabel === EServiceType.GGAP);
          const brcgsOrders = allOrders.filter((order): boolean => order.iceCubeLabel === EServiceType.BRCGS);
          const ifsOrders = allOrders.filter((order): boolean => order.iceCubeLabel === EServiceType.IFS);
          const sqfOrders = allOrders.filter((order): boolean => order.iceCubeLabel === EServiceType.SQF);
          const latestGgap =
            ggapOrders.length > 0
              ? ggapOrders.reduce(
                  (latest, current): IPreviousOrder =>
                    this._appTimeService.convertStringToSortableDate(current.orderDate) > this._appTimeService.convertStringToSortableDate(latest.orderDate) ? current : latest
                )
              : null;
          const latestBrcgs =
            brcgsOrders.length > 0
              ? brcgsOrders.reduce(
                  (latest, current): IPreviousOrder =>
                    this._appTimeService.convertStringToSortableDate(current.orderDate) > this._appTimeService.convertStringToSortableDate(latest.orderDate) ? current : latest
                )
              : null;
          const latestIfs =
            ifsOrders.length > 0
              ? ifsOrders.reduce(
                  (latest, current): IPreviousOrder =>
                    this._appTimeService.convertStringToSortableDate(current.orderDate) > this._appTimeService.convertStringToSortableDate(latest.orderDate) ? current : latest
                )
              : null;
          const latestSqf =
            sqfOrders.length > 0
              ? sqfOrders.reduce(
                  (latest, current): IPreviousOrder =>
                    this._appTimeService.convertStringToSortableDate(current.orderDate) > this._appTimeService.convertStringToSortableDate(latest.orderDate) ? current : latest
                )
              : null;

          orders = [latestGgap, latestBrcgs, latestIfs, latestSqf].filter(Boolean) as IPreviousOrder[];
          orders = sortBy(orders, (item): Date | number => (item.orderDate ? this._appTimeService.convertStringToSortableDate(item.orderDate) : Infinity)).reverse();
        } else {
          orders = this._displayResult(response[0], EBusinessSolution.INSPECTIONS).concat(
            this._displayResult(response[1], EBusinessSolution.AUDITS),
            this._displayResult(response[2], EBusinessSolution.LAB_TESTING)
          );
          orders = sortBy(orders, (item): Date | number =>
            item.originData.bookingDate ? this._appTimeService.convertStringToSortableDate(item.originData.bookingDate) : Infinity
          ).reverse();
        }

        this.isViewMoreButtonVisible = orders.length > 0;
        this.previousOrders = orders;
        this.isDataLoaded = true;
        this._changeDetectorRef.markForCheck();
      });
  }

  private _displayResult(orders: IPreviousOrders[], businessSolution: EBusinessSolution): IPreviousOrder[] {
    return orders.map((item: IPreviousOrders): IPreviousOrder => {
      const serviceDate: string = businessSolution === EBusinessSolution.LAB_TESTING ? item.testStartDate : (item.serviceDate ?? '');
      const productName: string = businessSolution === EBusinessSolution.AUDITS ? (item.supplierName ?? '') : item.productNames;
      let productNames: string[] = [];

      item.serviceDate = serviceDate ? this._appTimeService.capitalizeDateString(serviceDate) : '';

      if (productNames) {
        productNames = businessSolution === EBusinessSolution.INSPECTIONS ? productName.split('###') : productName.split(';');
      }

      return {
        iceCubeLabel: item.serviceTypeText,
        productNames,
        orderDate: serviceDate,
        originData: item,
        canActivate: SUPPORT_SERVICE_TYPES.includes(item.serviceTypeText),
      };
    });
  }
}
